import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentUploadMode } from '@core/enums/document-upload-mode.enum';
import { CaseDetailsWithEmailResult, PostRecertDocument, PostRecertDocumentValidate, StiiraError } from '@core/models';
import { DocumentUploadDialog, PostDocumentUpload, PostDocumentUploadForm, PostEmployeeDocumentUpload, PostEmployerDocumentUpload } from '@core/models/leave-admin/document-upload';
import { ErrorService, LayoutService, NotificationService, SnackbarService } from '@core/services';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { nameof, indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { PostTemplateDocumentUpload } from '@core/models/case-communication/template-document-upload.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';
import { EmailResult } from '@core/enums';
import { environment } from '@environments/environment';

@Component({
    selector: 'app-document-upload',
    templateUrl: './document-upload.component.html',
    styleUrls: ['./document-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentUploadComponent implements OnInit {
  static readonly componentName = 'DocumentUploadComponent';
  
  @ViewChild('imgFileInput') fileInput: ElementRef;
  @ViewChild('uploadButton', { read: ElementRef }) private uploadButton: ElementRef<HTMLButtonElement>;
  @Output() isEditing = new EventEmitter<boolean>();

  public fileToUpload: File;
  public form: FormGroup<PostDocumentUploadForm>;
  public isMobile$: Observable<boolean>;
  public isSubmitting$: Subject<boolean>;
  public docUploadMode = DocumentUploadMode;

  public position = position;
  public onDragEnded = onDragEnded;
  public showDateReceived: boolean = false;
 
  private formInitValues: any;
  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get formIsValid(): boolean {
    return this.form.valid;
  }

  get document(): FormControl<any> {
    return this.form.get(nameof<PostDocumentUploadForm>('document')) as FormControl<any>;
  }

  get documentTitle(): FormControl<string> {
    return this.form.get(nameof<PostDocumentUploadForm>('documentTitle')) as FormControl<string>;
  }

  get documentCategoryId(): FormControl<number> {
    return this.form.get(nameof<PostDocumentUploadForm>('documentCategoryId')) as FormControl<number>;
  }

  get dateReceived(): FormControl<Date> {
    return this.form.get(nameof<PostDocumentUploadForm>('dateReceived')) as FormControl<Date>;
  }

  get documentComments(): FormControl<string> {
    return this.form.get(nameof<PostDocumentUploadForm>('documentComments')) as FormControl<string>;
  }

  get associatedCase(): FormControl<number> {
    return this.form.get(nameof<PostDocumentUploadForm>('associatedCase')) as FormControl<number>;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      subjectId: number,
      docUploadDialog: DocumentUploadDialog;
      sysText: any;
      unsavedChangesSysText: any;
      mode: DocumentUploadMode;
      canSetDateReceived: boolean;
    },
    private dialogRef: MatDialogRef<DocumentUploadComponent>,
    private leaveAdminService: LeaveAdminService,
    private leaveAdminStore: LeaveAdminStoreService,
    private manageEmployersService: ManageEmployersService,
    private manageEmployeesService: ManageEmployeesService,
    private caseCommunicationService: CaseCommunicationService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private snackbar: SnackbarService,
    private notificationService: NotificationService,
    private layoutService: LayoutService
  ) {
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<PostDocumentUploadForm>({
        document: this.fb.control(null),
        documentTitle: this.fb.control(null, Validators.required),
        documentCategoryId: this.fb.control(null, Validators.required),
        dateReceived: this.fb.control(null),
        documentComments: this.fb.control(null),
        associatedCase: this.fb.control(null),
        sendNotification: this.fb.control(false)
      });
   }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    if (this.data.canSetDateReceived == true && (this.data.mode == this.docUploadMode.Employee || this.data.mode == this.docUploadMode.LeaveCase)) {
      this.showDateReceived = true;
      this.form.controls.dateReceived.setValue(new Date());
    }

    this.formInitValues = { ...this.form.value };

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        if (!this.noChanges && !this.formChangeEmitted) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    switch(this.data.mode) {
      case DocumentUploadMode.LeaveCase: {
        if (this.leaveAdminStore.isActionRunning) {
          this.openPendingChangesDialog();
          return;
        }

        const dto: PostDocumentUpload = {
          caseId: this.data.subjectId,
          document: this.fileToUpload ?? null,
          documentTitle: this.documentTitle.value,
          documentCategoryId: this.documentCategoryId.value,
          dateReceived: this.dateReceived?.value,
          documentComments: this.documentComments.value,
          sendNotification: this.data.docUploadDialog.showNotificationToggle
            ? this.form.controls.sendNotification.value
            : null
        };
    
        const formData = this.makePostDocumentUploadFormData(dto);
    
        this.leaveAdminService.postDocumentUpload(formData)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe((res) => { 
            this.dialogRef.close(res);
            this.handleLeaveCaseSuccessMessage(res);
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
        break;
      }
      case DocumentUploadMode.Employer: {
        const dto: PostEmployerDocumentUpload = {
          employerId: this.data.subjectId,
          document: this.fileToUpload ?? null,
          documentTitle: this.documentTitle.value,
          documentCategoryId: this.documentCategoryId.value,
          documentComments: this.documentComments.value
        };
    
        const formData = this.makePostEmployerDocumentUploadFormData(dto);
    
        this.manageEmployersService.postDocumentUpload(formData)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe((res) => {
              this.dialogRef.close(res);
              if (res) {
                let snackText =  this.data.sysText.successSnackBar;
                this.snackbar.open(snackText, this.data.sysText.dismiss);
              }
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
        break;
      }
      case DocumentUploadMode.Employee: {
        const dto: PostEmployeeDocumentUpload = {
          employeeId: this.data.subjectId,
          document: this.fileToUpload ?? null,
          documentTitle: this.documentTitle.value,
          documentCategoryId: this.documentCategoryId.value,
          dateReceived: this.dateReceived.value,
          documentComments: this.documentComments.value,
          associatedCase: this.associatedCase.value
        };
    
        const formData = this.makePostEmployeeDocumentUploadFormData(dto);
    
        this.manageEmployeesService.postDocumentUpload(formData)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe((res) => {
              this.dialogRef.close(res);
              if (res) {
                let snackText =  this.data.sysText.successSnackBar;
                this.snackbar.open(snackText, this.data.sysText.dismiss);
              }
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
        break;
      }
      case DocumentUploadMode.Template: {
        const dto: PostTemplateDocumentUpload = {
          templateId: this.data.subjectId,
          document: this.fileToUpload ?? null,
          documentTitle: this.documentTitle.value,
          documentCategoryId: this.documentCategoryId.value,
          documentComments: this.documentComments.value
        };
    
        const formData = this.makePostTemplateDocumentUploadFormData(dto);
    
        this.caseCommunicationService.postTemplateDocumentUpload(formData)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe((res) => {
              this.dialogRef.close(res);
              if (res) {
                let snackText =  this.data.sysText.successSnackBar;
                this.snackbar.open(snackText, this.data.sysText.dismiss);
              }
          },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
        break;
      }
      case DocumentUploadMode.Recert: {
        const dto: PostRecertDocumentValidate = {
          hasFile: !!this.fileToUpload, // just need to know if a file was selected
          documentTitle: this.documentTitle.value,
          documentCategoryId: this.documentCategoryId.value,
          documentComments: this.documentComments.value
        }

        const formData = this.makePostRecertDocumentValidateFormData(dto);

        this.leaveAdminService.postRecertDocumentValidate(formData)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe(
            (res) => {
              const dto: PostRecertDocument = {
                document: this.fileToUpload,
                documentTitle: this.documentTitle.value,
                documentCategoryId: this.documentCategoryId.value,
                documentComments: this.documentComments.value,
                modelStateErrors: null
              };

              this.dialogRef.close(dto);
              if (res) {
                let snackText =  this.data.sysText.successSnackBar;
                this.snackbar.open(snackText, this.data.sysText.dismiss);
              }
            },
            (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
          );

        break;
      }
    }
  }

  cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public clearFile(): void {
    // the pressed button goes with its file
    this.uploadButton.nativeElement.focus();
    this.fileToUpload = null;
    this.form.patchValue({
      documentTitle: null,
      documentCategoryId: null
    });
    this.fileInput.nativeElement.value = "";
    this.documentTitle.setValidators(null)
    this.documentCategoryId.setValidators(null)
    this.form.updateValueAndValidity();
  }

  public handleFileInput(files: FileList): void {
    const maxSize = environment.maxContentSizeMiB;

    if (files.item(0).size > 1024 * 1024 * maxSize) {
      this.document.setErrors({"msError": null}); // clear any previous errors first
      this.document.setErrors({"msError": this.data.sysText.documentTooLarge.replace("@[maxSize]", maxSize.toString())});
      this.notificationService.announceStatus(this.document.errors.msError);
      this.fileToUpload = null;
      this.form.updateValueAndValidity();
      return;
    }

    this.fileToUpload = files.item(0);
    this.notificationService.announceStatus(this.fileToUpload.name);
    this.document.setErrors({"msError": null});
    this.documentTitle.setValidators(Validators.required)
    this.documentCategoryId.setValidators(Validators.required)
    this.form.updateValueAndValidity();
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit();
      }
    });
  }

  private makePostDocumentUploadFormData (dto: PostDocumentUpload) {
    const formData = new FormData();
    formData.append(nameof<PostDocumentUpload>('caseId'), dto.caseId.toString())
    formData.append(nameof<PostDocumentUpload>('document'), dto.document)
    formData.append(nameof<PostDocumentUpload>('documentTitle'), dto.documentTitle)
    formData.append(nameof<PostDocumentUpload>('documentCategoryId'), dto.documentCategoryId ? dto.documentCategoryId.toString() : null)
    formData.append(nameof<PostDocumentUpload>('dateReceived'), dto.dateReceived ? dto.dateReceived.toISOString() : null)
    formData.append(nameof<PostDocumentUpload>('documentComments'), dto.documentComments)
    formData.append(nameof<PostDocumentUpload>('sendNotification'), dto.sendNotification?.toString() ?? null)

    return formData;
  }

  private makePostEmployerDocumentUploadFormData (dto: PostEmployerDocumentUpload) {
    const formData = new FormData();
    formData.append(nameof<PostEmployerDocumentUpload>('employerId'), dto.employerId.toString())
    formData.append(nameof<PostEmployerDocumentUpload>('document'), dto.document)
    formData.append(nameof<PostEmployerDocumentUpload>('documentTitle'), dto.documentTitle)
    formData.append(nameof<PostEmployerDocumentUpload>('documentCategoryId'), dto.documentCategoryId ? dto.documentCategoryId.toString() : null)
    formData.append(nameof<PostEmployerDocumentUpload>('documentComments'), dto.documentComments)

    return formData;
  }

  private makePostEmployeeDocumentUploadFormData (dto: PostEmployeeDocumentUpload) {
    const formData = new FormData();
    formData.append(nameof<PostEmployeeDocumentUpload>('employeeId'), dto.employeeId.toString())
    formData.append(nameof<PostEmployeeDocumentUpload>('document'), dto.document)
    formData.append(nameof<PostEmployeeDocumentUpload>('documentTitle'), dto.documentTitle)
    formData.append(nameof<PostEmployeeDocumentUpload>('documentCategoryId'), dto.documentCategoryId ? dto.documentCategoryId.toString() : null)
    formData.append(nameof<PostEmployeeDocumentUpload>('dateReceived'), dto.dateReceived ? dto.dateReceived.toISOString() : null)
    formData.append(nameof<PostEmployeeDocumentUpload>('documentComments'), dto.documentComments)
    formData.append(nameof<PostEmployeeDocumentUpload>('associatedCase'), dto.associatedCase ? dto.associatedCase.toString() : null)

    return formData;
  }

  private makePostTemplateDocumentUploadFormData (dto: PostTemplateDocumentUpload) {
    const formData = new FormData();
    formData.append(nameof<PostTemplateDocumentUpload>('templateId'), dto.templateId.toString())
    formData.append(nameof<PostTemplateDocumentUpload>('document'), dto.document)
    formData.append(nameof<PostTemplateDocumentUpload>('documentTitle'), dto.documentTitle)
    formData.append(nameof<PostTemplateDocumentUpload>('documentCategoryId'), dto.documentCategoryId ? dto.documentCategoryId.toString() : null)
    formData.append(nameof<PostTemplateDocumentUpload>('documentComments'), dto.documentComments)

    return formData;
  }

  private makePostRecertDocumentValidateFormData (dto: PostRecertDocumentValidate) {
    const formData = new FormData();
    formData.append(nameof<PostRecertDocumentValidate>('hasFile'), dto.hasFile.toString())
    formData.append(nameof<PostRecertDocumentValidate>('documentTitle'), dto.documentTitle)
    formData.append(nameof<PostRecertDocumentValidate>('documentCategoryId'), dto.documentCategoryId ? dto.documentCategoryId.toString() : null)
    formData.append(nameof<PostRecertDocumentValidate>('documentComments'), dto.documentComments)

    return formData;
  }

  private handleLeaveCaseSuccessMessage(res: CaseDetailsWithEmailResult): void {
    if (res?.emailResult == EmailResult.success) {
      this.snackbar.open(this.data.sysText.uploadDocumentWithEmailSuccess, this.data.sysText.dismiss);
    }
    else if (res?.emailResult == EmailResult.successWithFailure) {
      this.notificationService.showErrorToast(this.data.sysText.uploadDocumentWithEmailSuccessFailure);
    }
    else if (res?.emailResult == EmailResult.failure) {
      this.notificationService.showErrorToast(this.data.sysText.uploadDocumentWithEmailFailure);
    }
    else if (res?.emailResult == EmailResult.doesntApply) {
      this.snackbar.open(this.data.sysText.uploadDocumentWithoutEmail, this.data.sysText.dismiss);
    }
  }
}
